// Nutrition Tracking Manager
// Handles daily calorie and protein logging with Supabase integration

import { supabase } from '../supabaseClient';

/**
 * Get user_id (UUID) from username
 * @param {string} username
 * @returns {string|null} - UUID or null
 */
async function getUserIdFromUsername(username) {
  if (!username) return null;

  const { data, error } = await supabase
    .from('users')
    .select('id')
    .eq('username', username)
    .maybeSingle();

  if (error || !data) {
    console.error('Error fetching user ID:', error);
    return null;
  }

  return data.id;
}

/**
 * Save a nutrition entry for a specific date (can be multiple meals per day)
 * @param {Object} nutritionData - { date, calories, protein, mealName, notes }
 * @returns {Object} - { success, data, error }
 */
export async function saveNutritionEntry(nutritionData) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      throw new Error('Could not get user ID');
    }

    const { data, error } = await supabase
      .from('nutrition_logs')
      .insert([{
        user_id: userId,
        log_date: nutritionData.date,
        food_description: nutritionData.mealName || 'Food Entry',
        calories: nutritionData.calories,
        protein: nutritionData.protein,
        servings: nutritionData.servings || 1,
        serving_size: nutritionData.servingSize || null
      }])
      .select(); // Add .select() to get back the inserted data with ID

    if (error) {
      console.error('⚠️ Supabase nutrition entry FAILED - falling back to localStorage:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      // Fallback to localStorage if Supabase fails
      const localResult = saveNutritionEntryLocal(nutritionData);
      return { ...localResult, localOnly: true, supabaseError: error.message };
    }

    console.log('✅ Nutrition entry saved to Supabase successfully');
    return { success: true, data: data[0] };
  } catch (err) {
    console.error('⚠️ Exception saving nutrition entry - falling back to localStorage:', err);
    // Fallback to localStorage
    const localResult = saveNutritionEntryLocal(nutritionData);
    return { ...localResult, localOnly: true, error: err.message };
  }
}

/**
 * Get all nutrition entries for a date range
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string
 * @returns {Array} - Array of nutrition entries
 */
export async function getNutritionEntries(startDate, endDate) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return getNutritionEntriesLocal(startDate, endDate);
    }

    const { data, error} = await supabase
      .from('nutrition_logs')
      .select('*')
      .eq('user_id', userId)
      .gte('log_date', startDate)
      .lte('log_date', endDate)
      .order('log_date', { ascending: true });

    if (error) {
      console.error('Supabase get nutrition error:', error);
      return getNutritionEntriesLocal(startDate, endDate);
    }

    return data || [];
  } catch (err) {
    console.error('Error getting nutrition entries:', err);
    return getNutritionEntriesLocal(startDate, endDate);
  }
}

/**
 * Get all nutrition entries for a specific date (supports multiple meals)
 * @param {string} date - ISO date string (YYYY-MM-DD)
 * @returns {Array} - Array of nutrition entries for the date
 */
export async function getNutritionForDate(date) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return getNutritionForDateLocal(date);
    }

    const { data, error } = await supabase
      .from('nutrition_logs')
      .select('*')
      .eq('user_id', userId)
      .eq('log_date', date)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Supabase get nutrition for date error:', error);
      return getNutritionForDateLocal(date);
    }

    return data || [];
  } catch (err) {
    console.error('Error getting nutrition for date:', err);
    return getNutritionForDateLocal(date);
  }
}

/**
 * Delete a nutrition entry by ID
 * @param {number} entryId - Entry ID from database
 * @returns {Object} - { success, error }
 */
export async function deleteNutritionEntry(entryId) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return deleteNutritionEntryLocal(entryId);
    }

    const { error } = await supabase
      .from('nutrition_logs')
      .delete()
      .eq('id', entryId)
      .eq('user_id', userId);

    if (error) {
      console.error('Supabase delete nutrition error:', error);
      return deleteNutritionEntryLocal(entryId);
    }

    return { success: true };
  } catch (err) {
    console.error('Error deleting nutrition entry:', err);
    return deleteNutritionEntryLocal(entryId);
  }
}

/**
 * Update nutrition entry by ID
 * @param {number} entryId - Entry ID
 * @param {Object} updates - { calories, protein, mealName, notes }
 * @returns {Object} - { success, data, error }
 */
export async function updateNutritionEntry(entryId, updates) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return updateNutritionEntryLocal(entryId, updates);
    }

    const { data, error } = await supabase
      .from('nutrition_logs')
      .update(updates)
      .eq('id', entryId)
      .eq('user_id', userId);

    if (error) {
      console.error('Supabase update nutrition error:', error);
      return updateNutritionEntryLocal(entryId, updates);
    }

    return { success: true, data };
  } catch (err) {
    console.error('Error updating nutrition entry:', err);
    return updateNutritionEntryLocal(entryId, updates);
  }
}

/**
 * Calculate nutrition summary for a date range
 * @param {Array} entries - Array of nutrition entries
 * @param {Object} goals - { calorieGoal, proteinGoal }
 * @returns {Object} - Summary statistics
 */
export function calculateNutritionSummary(entries, goals) {
  if (!entries || entries.length === 0) {
    return {
      totalCalories: 0,
      totalProtein: 0,
      avgCalories: 0,
      avgProtein: 0,
      daysLogged: 0,
      daysOnTarget: 0,
      calorieVariance: 0,
      proteinVariance: 0
    };
  }

  const totalCalories = entries.reduce((sum, entry) => sum + (entry.calories || 0), 0);
  const totalProtein = entries.reduce((sum, entry) => sum + (entry.protein || 0), 0);
  const avgCalories = totalCalories / entries.length;
  const avgProtein = totalProtein / entries.length;

  // Calculate how many days were on target (within 10% of goal)
  const daysOnTarget = entries.filter(entry => {
    const calorieOnTarget = goals.calorieGoal
      ? Math.abs(entry.calories - goals.calorieGoal) <= (goals.calorieGoal * 0.1)
      : false;
    const proteinOnTarget = goals.proteinGoal
      ? entry.protein >= (goals.proteinGoal * 0.9) // At least 90% of protein goal
      : false;
    return calorieOnTarget && proteinOnTarget;
  }).length;

  // Calculate variance from goals
  const calorieVariance = goals.calorieGoal ? avgCalories - goals.calorieGoal : 0;
  const proteinVariance = goals.proteinGoal ? avgProtein - goals.proteinGoal : 0;

  return {
    totalCalories,
    totalProtein,
    avgCalories: Math.round(avgCalories),
    avgProtein: Math.round(avgProtein),
    daysLogged: entries.length,
    daysOnTarget,
    calorieVariance: Math.round(calorieVariance),
    proteinVariance: Math.round(proteinVariance)
  };
}

// ==================== LocalStorage Fallback Functions ====================

function saveNutritionEntryLocal(nutritionData) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `nutrition_${username}`;
    const existing = localStorage.getItem(key);
    const entries = existing ? JSON.parse(existing) : [];

    // Always add new entry (support multiple meals per day)
    const newEntry = {
      id: Date.now(), // Generate a temporary ID for local entries
      log_date: nutritionData.date,
      food_description: nutritionData.mealName || 'Food Entry',
      calories: nutritionData.calories || 0,
      protein: nutritionData.protein || 0,
      servings: nutritionData.servings || 1,
      serving_size: nutritionData.servingSize || null,
      created_at: new Date().toISOString()
    };

    entries.push(newEntry);

    localStorage.setItem(key, JSON.stringify(entries));
    return { success: true, data: newEntry };
  } catch (err) {
    console.error('LocalStorage nutrition save error:', err);
    return { success: false, error: err };
  }
}

function getNutritionEntriesLocal(startDate, endDate) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `nutrition_${username}`;
    const existing = localStorage.getItem(key);
    const entries = existing ? JSON.parse(existing) : [];

    return entries.filter(entry => {
      const entryDate = entry.log_date || entry.date; // Support old format
      return entryDate >= startDate && entryDate <= endDate;
    }).sort((a, b) => {
      const dateA = a.log_date || a.date;
      const dateB = b.log_date || b.date;
      return dateA.localeCompare(dateB);
    });
  } catch (err) {
    console.error('LocalStorage get nutrition error:', err);
    return [];
  }
}

function getNutritionForDateLocal(date) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `nutrition_${username}`;
    const existing = localStorage.getItem(key);
    const entries = existing ? JSON.parse(existing) : [];

    return entries.filter(entry => {
      const entryDate = entry.log_date || entry.date; // Support old format
      return entryDate === date;
    }).sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });
  } catch (err) {
    console.error('LocalStorage get nutrition for date error:', err);
    return [];
  }
}

function deleteNutritionEntryLocal(entryId) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `nutrition_${username}`;
    const existing = localStorage.getItem(key);
    const entries = existing ? JSON.parse(existing) : [];

    const filtered = entries.filter(entry => entry.id !== entryId);
    localStorage.setItem(key, JSON.stringify(filtered));
    return { success: true };
  } catch (err) {
    console.error('LocalStorage delete nutrition entry error:', err);
    return { success: false, error: err };
  }
}

function updateNutritionEntryLocal(entryId, updates) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `nutrition_${username}`;
    const existing = localStorage.getItem(key);
    const entries = existing ? JSON.parse(existing) : [];

    const index = entries.findIndex(entry => entry.id === entryId);

    if (index >= 0) {
      entries[index] = {
        ...entries[index],
        ...updates,
        updated_at: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(entries));
      return { success: true, data: entries[index] };
    }

    return { success: false, error: 'Entry not found' };
  } catch (err) {
    console.error('LocalStorage update nutrition error:', err);
    return { success: false, error: err };
  }
}

/**
 * Get current week's nutrition entries (Monday - Sunday)
 * @returns {Array} - Nutrition entries for current week
 */
export async function getCurrentWeekNutrition() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const startDate = monday.toISOString().split('T')[0];
  const endDate = sunday.toISOString().split('T')[0];

  return getNutritionEntries(startDate, endDate);
}

// ==================== Saved Food Items Functions ====================

/**
 * Save a food item to user's common items (user-specific saved foods)
 * @param {Object} foodItem - { name, brandName, calories, protein, totalFat, totalCarbohydrate, sugars, dietaryFiber, servingQty, servingUnit, servingWeightGrams, photoUrl, source, nixItemId, upc }
 * @returns {Object} - { success, data, error }
 */
export async function saveFoodItem(foodItem) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return saveFoodItemLocal(foodItem);
    }

    const { data, error } = await supabase
      .from('user_saved_foods')
      .insert([{
        user_id: userId,
        food_name: foodItem.name,
        brand_name: foodItem.brandName || null,
        serving_qty: foodItem.servingQty || 1,
        serving_unit: foodItem.servingUnit || 'serving',
        serving_weight_grams: foodItem.servingWeightGrams || null,
        calories: foodItem.calories,
        protein: foodItem.protein || 0,
        total_fat: foodItem.totalFat || 0,
        total_carbohydrate: foodItem.totalCarbohydrate || 0,
        sugars: foodItem.sugars || 0,
        dietary_fiber: foodItem.dietaryFiber || 0,
        photo_url: foodItem.photoUrl || null,
        source: foodItem.source || 'manual',
        nix_item_id: foodItem.nixItemId || null,
        upc: foodItem.upc || null
      }])
      .select();

    if (error) {
      console.error('Supabase save food item error:', error);
      return saveFoodItemLocal(foodItem);
    }

    return { success: true, data: data[0] };
  } catch (err) {
    console.error('Error saving food item:', err);
    return saveFoodItemLocal(foodItem);
  }
}

/**
 * Get all saved food items for current user (sorted by last used)
 * @returns {Array} - Array of saved food items
 */
export async function getSavedFoodItems() {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return getSavedFoodItemsLocal();
    }

    const { data, error } = await supabase
      .from('user_saved_foods')
      .select('*')
      .eq('user_id', userId)
      .order('last_used_at', { ascending: false });

    if (error) {
      console.error('Supabase get food items error:', error);
      return getSavedFoodItemsLocal();
    }

    return data || [];
  } catch (err) {
    console.error('Error getting saved food items:', err);
    return getSavedFoodItemsLocal();
  }
}

/**
 * Delete a saved food item
 * @param {string} itemId - Food item ID (UUID)
 * @returns {Object} - { success, error }
 */
export async function deleteFoodItem(itemId) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return deleteFoodItemLocal(itemId);
    }

    const { error } = await supabase
      .from('user_saved_foods')
      .delete()
      .eq('id', itemId)
      .eq('user_id', userId);

    if (error) {
      console.error('Supabase delete food item error:', error);
      return deleteFoodItemLocal(itemId);
    }

    return { success: true };
  } catch (err) {
    console.error('Error deleting food item:', err);
    return deleteFoodItemLocal(itemId);
  }
}

// LocalStorage fallback for saved food items
function saveFoodItemLocal(foodItem) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `food_items_${username}`;
    const existing = localStorage.getItem(key);
    const items = existing ? JSON.parse(existing) : [];

    const newItem = {
      id: Date.now().toString(),
      food_name: foodItem.name,
      brand_name: foodItem.brandName || null,
      serving_qty: foodItem.servingQty || 1,
      serving_unit: foodItem.servingUnit || 'serving',
      serving_weight_grams: foodItem.servingWeightGrams || null,
      calories: foodItem.calories,
      protein: foodItem.protein || 0,
      total_fat: foodItem.totalFat || 0,
      total_carbohydrate: foodItem.totalCarbohydrate || 0,
      sugars: foodItem.sugars || 0,
      dietary_fiber: foodItem.dietaryFiber || 0,
      photo_url: foodItem.photoUrl || null,
      source: foodItem.source || 'manual',
      nix_item_id: foodItem.nixItemId || null,
      upc: foodItem.upc || null,
      created_at: new Date().toISOString(),
      last_used_at: new Date().toISOString(),
      use_count: 0
    };

    items.push(newItem);
    localStorage.setItem(key, JSON.stringify(items));
    return { success: true, data: newItem };
  } catch (err) {
    console.error('LocalStorage save food item error:', err);
    return { success: false, error: err };
  }
}

function getSavedFoodItemsLocal() {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `food_items_${username}`;
    const existing = localStorage.getItem(key);
    const items = existing ? JSON.parse(existing) : [];
    return items.sort((a, b) => {
      // Sort by last_used_at descending (most recent first)
      const dateA = new Date(a.last_used_at || a.created_at);
      const dateB = new Date(b.last_used_at || b.created_at);
      return dateB - dateA;
    });
  } catch (err) {
    console.error('LocalStorage get food items error:', err);
    return [];
  }
}

function deleteFoodItemLocal(itemId) {
  try {
    const username = localStorage.getItem('currentUser');
    const key = `food_items_${username}`;
    const existing = localStorage.getItem(key);
    const items = existing ? JSON.parse(existing) : [];

    const filtered = items.filter(item => item.id !== itemId);
    localStorage.setItem(key, JSON.stringify(filtered));
    return { success: true };
  } catch (err) {
    console.error('LocalStorage delete food item error:', err);
    return { success: false, error: err };
  }
}

/**
 * Update usage tracking when a saved food is selected
 * @param {string} itemId - Food item ID (UUID)
 * @returns {Object} - { success, error }
 */
export async function updateFoodItemUsage(itemId) {
  try {
    const username = localStorage.getItem('currentUser');
    if (!username) {
      throw new Error('No user logged in');
    }

    const userId = await getUserIdFromUsername(username);
    if (!userId) {
      return { success: true }; // Skip for local storage
    }

    // First get current use_count
    const { data: currentData, error: fetchError } = await supabase
      .from('user_saved_foods')
      .select('use_count')
      .eq('id', itemId)
      .eq('user_id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching current use_count:', fetchError);
      return { success: true };
    }

    // Update with incremented use_count
    const { error } = await supabase
      .from('user_saved_foods')
      .update({
        last_used_at: new Date().toISOString(),
        use_count: (currentData?.use_count || 0) + 1
      })
      .eq('id', itemId)
      .eq('user_id', userId);

    if (error) {
      console.error('Supabase update food usage error:', error);
      // Non-critical error, don't fail the operation
      return { success: true };
    }

    return { success: true };
  } catch (err) {
    console.error('Error updating food usage:', err);
    // Non-critical error, don't fail the operation
    return { success: true };
  }
}

// ==================== Default Food Items ====================
// REMOVED: Default food items system replaced with user-specific saved foods
// Users can now save foods from API/barcode lookups to their personal list

// ==================== AI Meal Estimation ====================

/**
 * Use Claude AI to estimate calories and protein for a meal description
 * @param {string} mealDescription - Natural language description of the meal
 * @returns {Object} - { success, calories, protein, mealName, error }
 */
export async function estimateMealNutrition(mealDescription) {
  try {
    if (!mealDescription || mealDescription.trim().length < 3) {
      return { success: false, error: 'Please provide a meal description' };
    }

    const prompt = `You are a nutrition expert. Analyze this meal description and provide estimated nutritional information.

Meal description: "${mealDescription}"

If this is a COMPLEX meal with multiple distinct components (like "burger and fries", "steak and potatoes", "chicken with rice"), break it down into separate items. Otherwise, treat it as a single item.

Provide your response in this EXACT format:

COMPLEX: [YES or NO - YES if meal has 2+ distinct components that should be tracked separately]

If COMPLEX=YES, provide each component:
ITEM_1_NAME: [name]
ITEM_1_CALORIES: [number]
ITEM_1_PROTEIN: [number]
ITEM_1_SERVING: [serving size]

ITEM_2_NAME: [name]
ITEM_2_CALORIES: [number]
ITEM_2_PROTEIN: [number]
ITEM_2_SERVING: [serving size]

(Continue with ITEM_3, ITEM_4, etc. if needed)

If COMPLEX=NO, provide single item:
MEAL_NAME: [cleaned up meal name, max 50 characters]
CALORIES: [number]
PROTEIN: [number]
SERVING_SIZE: [serving size]

IMPORTANT for serving sizes:
- Solid foods: Express in ounces (oz). Examples: "6 oz", "8 oz", "4 oz"
- Liquids/soups: Express in cups. Examples: "1 cup", "1.5 cups", "2 cups"
- For items that come in pieces (wings, slices, etc): Express as count. Examples: "6 wings", "2 slices", "1 burger"

Be realistic with portions. If no portion size is mentioned, assume typical restaurant serving sizes.

Your response:`;

    // Use Netlify function as proxy (same as weekly planner)
    const response = await fetch('/.netlify/functions/claude-api', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'claude-3-haiku-20240307',
        max_tokens: 300,
        messages: [{
          role: 'user',
          content: prompt
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Claude API error:', errorData);
      return { success: false, error: 'Failed to get AI estimation. Please try manual entry.' };
    }

    const data = await response.json();
    const aiResponse = data.content[0].text;

    // Check if it's a complex meal
    const complexMatch = aiResponse.match(/COMPLEX:\s*(YES|NO)/i);
    const isComplex = complexMatch && complexMatch[1].toUpperCase() === 'YES';

    if (isComplex) {
      // Parse multiple items
      const items = [];
      let itemNumber = 1;

      while (true) {
        const nameMatch = aiResponse.match(new RegExp(`ITEM_${itemNumber}_NAME:\\s*(.+?)(?:\\n|$)`, 'i'));
        const caloriesMatch = aiResponse.match(new RegExp(`ITEM_${itemNumber}_CALORIES:\\s*(\\d+)`, 'i'));
        const proteinMatch = aiResponse.match(new RegExp(`ITEM_${itemNumber}_PROTEIN:\\s*(\\d+)`, 'i'));
        const servingMatch = aiResponse.match(new RegExp(`ITEM_${itemNumber}_SERVING:\\s*(.+?)(?:\\n|$)`, 'i'));

        if (!nameMatch || !caloriesMatch || !proteinMatch) {
          break; // No more items
        }

        items.push({
          mealName: nameMatch[1].trim(),
          calories: parseInt(caloriesMatch[1]),
          protein: parseInt(proteinMatch[1]),
          servingSize: servingMatch ? servingMatch[1].trim() : '1 serving'
        });

        itemNumber++;
      }

      if (items.length === 0) {
        console.error('Failed to parse complex meal items:', aiResponse);
        return { success: false, error: 'Could not parse meal components. Please try again.' };
      }

      return {
        success: true,
        isComplex: true,
        items: items,
        aiGenerated: true
      };

    } else {
      // Parse single item
      const caloriesMatch = aiResponse.match(/CALORIES:\s*(\d+)/i);
      const proteinMatch = aiResponse.match(/PROTEIN:\s*(\d+)/i);
      const servingSizeMatch = aiResponse.match(/SERVING_SIZE:\s*(.+?)(?:\n|$)/i);
      const mealNameMatch = aiResponse.match(/MEAL_NAME:\s*(.+?)(?:\n|$)/i);

      if (!caloriesMatch || !proteinMatch) {
        console.error('Failed to parse AI response:', aiResponse);
        return { success: false, error: 'Could not parse nutrition data. Please try again.' };
      }

      const calories = parseInt(caloriesMatch[1]);
      const protein = parseInt(proteinMatch[1]);
      const servingSize = servingSizeMatch ? servingSizeMatch[1].trim() : '1 serving';
      const mealName = mealNameMatch ? mealNameMatch[1].trim() : mealDescription;

      return {
        success: true,
        isComplex: false,
        calories,
        protein,
        servingSize,
        mealName,
        aiGenerated: true
      };
    }

  } catch (err) {
    console.error('Error estimating meal nutrition:', err);
    return { success: false, error: 'Failed to connect to AI service. Please try manual entry.' };
  }
}
